import { useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { updateFilters } from "store/filters/operations"; // Use updateFilters instead
import { selectFilters } from "store/filters/selectors";
import { Checkbox, CustomPlaceholder } from "reusableComponents";
import { FiltersButton } from "..";
import { getPlaceStyles } from "services/homePage.services";
import { useTranslation } from "react-i18next";
import { useLanguage } from "context/languageContext";
import styles from "./styles.module.scss";

const StylesFilter = ({ withStyledBtn = true }) => {
  const [t] = useTranslation("global");
  const dispatch = useDispatch();
  const filters = useSelector(selectFilters);
  const [showStylesDropdown, setShowStylesDropdown] = useState(false);
  const [stylesOptions, setStylesOptions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const stylesDropdownRef = useRef(null);
  const { language } = useLanguage();

  useEffect(() => {
    const fetchStyles = async () => {
      setLoading(true);
      setError(null);
      try {
        const stylesData = await getPlaceStyles(language);
        setStylesOptions([
          { id: null, name: t("search.allStyles") || "All Styles" },
          ...stylesData.map(style => ({
            id: style.id,
            name: style.name
          }))
        ]);
      } catch (err) {
        setError(err.message);
        console.error("Failed to fetch styles:", err);
      } finally {
        setLoading(false);
      }
    };

    fetchStyles();

    const handleClickOutside = (event) => {
      if (
        stylesDropdownRef.current &&
        !stylesDropdownRef.current.contains(event.target)
      ) {
        setShowStylesDropdown(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [language, t]);

  const toggleShowStylesDropdown = () => {
    setShowStylesDropdown((prevState) => !prevState);
  };

  const handleStyleChange = (selectedStyle) => {
    console.log("Selected style:", selectedStyle);
    console.log("Current filters.style:", filters.style);
    
    // Use updateFilters instead - it's simpler and should work
    dispatch(updateFilters({ style: selectedStyle.id }));
    
    console.log("Dispatch completed, style should be set to:", selectedStyle.id);
    
    setShowStylesDropdown(false);
  };

  // Debug: log filter changes
  useEffect(() => {
    console.log("=== FILTERS UPDATED ===");
    console.log("Current style filter:", filters.style);
    console.log("Full filters object:", filters);
  }, [filters.style]);

  // Get the name of the selected style for the placeholder
  const getSelectedStyleName = () => {
    if (!filters.style) {
      return t("search.allStyles") || "All Styles";
    }
    const selectedStyle = stylesOptions.find(style => style.id === filters.style);
    return selectedStyle ? selectedStyle.name : (t("search.allStyles") || "All Styles");
  };

  // Get display text for the button/placeholder
  const getDisplayText = () => {
    if (loading) return t("search.loading") || "Loading...";
    if (error) return t("search.defaultStyle") || "Style";
    return getSelectedStyleName();
  };

  // Check if a style option is selected
  const isStyleSelected = (style) => {
    if (style.id === null) {
      return !filters.style;
    }
    return filters.style === style.id;
  };

  return (
    <div className={styles.stylesWrapper} ref={stylesDropdownRef}>
      {withStyledBtn ? (
        <FiltersButton
          label={getDisplayText()}
          isActive={!!filters.style}
          onClick={toggleShowStylesDropdown}
          disabled={loading}
        />
      ) : (
        <CustomPlaceholder
          condition={!!filters.style}
          text={getDisplayText()}
          defaultText={t("search.defaultStyle") || "Style"}
          onClick={toggleShowStylesDropdown}
          disabled={loading}
        />
      )}
      
      <div
        className={`${styles.stylesDropdownBody} ${
          showStylesDropdown ? styles.visible : styles.hidden
        }`}
      >
        {loading ? (
          <div className={styles.loadingOption}>
            {t("search.loading") || "Loading..."}
          </div>
        ) : error ? (
          <div className={styles.errorOption}>
            {t("search.errorLoadingStyles") || "Error loading styles"}
          </div>
        ) : (
          stylesOptions.map((style) => (
            <div 
              key={style.id || "all"} 
              className={styles.styleOption}
              onClick={() => handleStyleChange(style)}
            >
              <Checkbox
                checked={isStyleSelected(style)}
                onChange={() => {}} // Empty function since we're handling click on the div
                label={style.name}
                readOnly
              />
            </div>
          ))
        )}
      </div>
    </div>
  );
};

export default StylesFilter;